import {
  InteractionType,
  PublicEWebinarFragment,
  InteractionForAttendeeFragment,
  InteractionValueType,
  RoomType,
  AttendeeFragment,
} from '$/GraphQL/schema/schema';

import { Rooms } from '$/components/WebinarPlayer/Rooms/Rooms';
import type {
  IntegrationInfo,
  InteractionTypeInfo,
} from '$/sections/Home/Profile/TeamIntegrations/Info';
import { ThinkificPromoInteractionInfo } from '$/sections/Home/Profile/TeamIntegrations/Info/Thinkific/ThinkificPromoInteractionInfo';
import { ProductHuntPromoInteractionInfo } from '$/sections/Home/Profile/TeamIntegrations/Info/ProductHunt/ProductHuntPromoInteractionInfo';
import { InteractionInfo } from './InteractionInfo';
import { AnchorInfo } from './Anchor/AnchorInfo';
import { ContactRequestInfo } from './ContactRequest/ContactRequestInfo';
import { ConversionNotificationsInfo } from './ConversionNotifications/ConversionNotificationsInfo';
import { FeedbackInfo } from './Feedback/FeedbackInfo';
import { HandoutInfo } from './Handout/HandoutInfo';
import { ImageOverlayInfo } from './ImageOverlay/ImageOverlayInfo';
import { IoradLinkInfo } from './IoradLink/IoradLinkInfo';
import { NextWebinarInfo } from './NextWebinar/NextWebinarInfo';
import { OfferInfo } from './Offer/OfferInfo';
import { PollInfo } from './Poll/PollInfo';
import { PrivateMessageInfo } from './PrivateMessage/PrivateMessageInfo';
import { QuestionInfo } from './Question/QuestionInfo';
import { QuizInfo } from './Quiz/QuizInfo';
import { QuizSummaryInfo } from './Quiz/QuizSummaryInfo';
import { StreamInfo } from './StreamInfo';
import { TestimonialInfo } from './Testimonial/TestimonialInfo';
import { TextOverlayInfo } from './TextOverlay/TextOverlayInfo';
import { TipInfo } from './Tip/TipInfo';
import { TipWithKoFiInfo } from './TipWithKoFi/TipWithKoFiInfo';
import { InVideoRegistrationInfo } from './InVideoRegistration/InVideoRegistrationInfo';

type ExtensibleInteractionType = string;

export class InteractionFactory {
  public loading = true;
  public integrations: IntegrationInfo[] = [];
  public cardIntegrations: IntegrationInfo[] = [];

  public integrationForType(type: string): IntegrationInfo | undefined {
    for (const integration of this.cardIntegrations) {
      for (const typeInfo of integration.interactionTypes()) {
        if (typeInfo.type === type) {
          return integration;
        }
      }
    }

    return undefined;
  }

  public from(
    interaction: InteractionForAttendeeFragment,
    ewebinar?: PublicEWebinarFragment | null,
    attendee?: AttendeeFragment
  ): InteractionInfo {
    ewebinar = ewebinar ?? undefined;

    switch (interaction.type) {
      case InteractionType.Handout:
        return new HandoutInfo(interaction, ewebinar, attendee);

      case InteractionType.IoradLink:
        return new IoradLinkInfo(interaction, ewebinar, attendee);

      case InteractionType.Question:
        return new QuestionInfo(interaction, ewebinar, attendee);
      case InteractionType.Feedback:
        return new FeedbackInfo(interaction, ewebinar, attendee);

      case InteractionType.RequestToContact:
        return new ContactRequestInfo(interaction, ewebinar, attendee);

      case InteractionType.EndStream:
        return new StreamInfo(interaction, ewebinar, attendee);

      case InteractionType.Anchor:
        return new AnchorInfo(interaction, ewebinar, attendee);

      case InteractionType.Tip:
        return new TipInfo(interaction, ewebinar, attendee);

      case InteractionType.Poll:
        return new PollInfo(interaction, ewebinar, attendee);

      case InteractionType.Quiz:
        return new QuizInfo(interaction, ewebinar, attendee);

      case InteractionType.QuizSummary:
        return new QuizSummaryInfo(interaction, ewebinar, attendee);

      case InteractionType.SpecialOffer:
        return new OfferInfo(interaction, ewebinar, attendee);

      case InteractionType.ConversionNotifications:
        return new ConversionNotificationsInfo(interaction, ewebinar, attendee);

      case InteractionType.LogoOverlay:
        return new ImageOverlayInfo(interaction, ewebinar, attendee);

      case InteractionType.TextOverlay:
        return new TextOverlayInfo(interaction, ewebinar, attendee);

      case InteractionType.PrivateMessage:
        return new PrivateMessageInfo(interaction, ewebinar, attendee);

      case InteractionType.NextWebinar:
        return new NextWebinarInfo(interaction, ewebinar, attendee);

      case InteractionType.TipWithKoFi:
        return new TipWithKoFiInfo(interaction, ewebinar, attendee);

      case InteractionType.Testimonial:
        return new TestimonialInfo(interaction, ewebinar, attendee);

      case InteractionType.InVideoRegistration:
        return new InVideoRegistrationInfo(interaction, ewebinar, attendee);

      case 'Thinkific-Promo':
        return new ThinkificPromoInteractionInfo(
          interaction,
          this.integrationForType(interaction.type),
          ewebinar,
          attendee
        );

      case 'ProductHunt-Promo':
        return new ProductHuntPromoInteractionInfo(
          interaction,
          this.integrationForType(interaction.type),
          ewebinar,
          attendee
        );
    }

    return new TipInfo(interaction, ewebinar, attendee);
  }

  public create(
    type: ExtensibleInteractionType,
    interactions: InteractionInfo[] | undefined,
    playbackPosition = 0,
    ewebinar?: PublicEWebinarFragment | null
  ): InteractionInfo {
    let appearAt = 0;
    let room = RoomType.Presentation;
    const rooms = Rooms.create(ewebinar || undefined);

    room = rooms.getRoomAt(playbackPosition).type;
    const [appear, roomType] = rooms.appearAtDuoFromPosition(playbackPosition);
    appearAt = appear;

    if (ewebinar && room !== roomType) {
      console.error('BUG: Room types should match');
    }
    const now = new Date(Date.now());

    const ifragment = {
      id: '',
      interactionShortId: '',
      type,
      valueType: InteractionValueType.Action,
      room,
      appearAt,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };

    const int = this.from(ifragment, ewebinar);
    int.fragment.details = {
      ...int.detailsDefault(interactions ?? []),
      ...int.detailsDuplicateDefault(interactions ?? []),
    };

    return int;
  }

  public analyticsFilterTypes(): string[] {
    const internal = Object.values(InteractionType).filter(
      (t) => !InteractionInfo.nonEditableTypes.includes(t)
    );

    const integrations = ([] as InteractionTypeInfo[]).concat(
      ...this.cardIntegrations.map((i) => i.interactionTypes())
    );

    return [...internal, ...integrations.map((i) => i.type)];
  }

  public allTypes(interactions: InteractionInfo[]): InteractionInfo[] {
    return this.analyticsFilterTypes()
      .map((type) => this.create(type, interactions, 0, null))
      .sort((a, b) => (b.typeName() ?? '').localeCompare(a.typeName() ?? ''));
  }
}
